<template>
  <div class="auth-page">
    <div class="auth-brand">
      <span class="auth-brand__mark">27pub</span>
      <span class="auth-brand__sub">Poker Management</span>
    </div>

    <v-card class="auth-card" elevation="2">
      <div class="auth-card-header">
        <h2>Sign in</h2>
        <p>Know where you stand. Every hand.</p>
      </div>
      <v-card-text class="pa-6">
        <v-form @submit.prevent="handleLogin">
          <v-text-field
            label="Email"
            v-model="useremail"
            type="email"
            required
            variant="outlined"
            density="comfortable"
            class="mb-2"
          ></v-text-field>
          <v-text-field
            label="Password"
            v-model="password"
            type="password"
            required
            variant="outlined"
            density="comfortable"
            class="mb-4"
            @keydown.enter.prevent="handleLogin"
          ></v-text-field>
          <v-alert v-if="feedback" type="error" density="compact" class="mb-4">
            {{ feedback }}
          </v-alert>
          <v-btn color="primary" type="submit" block size="large" class="mb-3">
            Sign in
          </v-btn>
          <v-btn variant="text" size="small" block @click="openForgotDialog" class="text-medium-emphasis">
            Forgot password?
          </v-btn>
        </v-form>
      </v-card-text>
    </v-card>

    <div class="auth-links">
      <span class="text-medium-emphasis">Don't have an account?</span>
      <router-link to="/signup">Join the Beta</router-link>
    </div>

    <div class="auth-footer">
      <router-link to="/">← Back to home</router-link>
    </div>

    <!-- Public tournament watch link -->
    <div class="auth-extra">
      <span>Watching a tournament?</span>
      <a href="http://www.27pub.com/vt" target="_blank" rel="noopener">Open the viewer →</a>
    </div>

    <!-- Forgot password dialog -->
    <v-dialog v-model="forgotDialog" max-width="420">
      <v-card>
        <v-card-title class="text-h6 pa-4">Reset password</v-card-title>
        <v-card-text>
          <v-text-field
            label="Email"
            v-model="forgotEmail"
            type="email"
            required
            variant="outlined"
            density="comfortable"
          ></v-text-field>
          <v-alert v-if="forgotFeedback" :type="forgotFeedbackType" density="compact">
            {{ forgotFeedback }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="forgotDialog = false">Cancel</v-btn>
          <v-btn :loading="forgotLoading" color="primary" variant="text" @click="sendResetEmail">Send</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useUserStore } from '@/stores/users'
import { useRouter } from 'vue-router'
import { getAuth, sendPasswordResetEmail } from 'firebase/auth'
import { useUiStore } from '@/stores/ui'

const router = useRouter()
const userStore = useUserStore()
const uiStore = useUiStore()

onMounted(() => { uiStore.navBarVisible = false })
onUnmounted(() => { uiStore.navBarVisible = true })

const useremail = ref("")
const password = ref("")
const feedback = ref("")

const forgotDialog = ref(false)
const forgotEmail = ref('')
const forgotFeedback = ref('')
const forgotFeedbackType = ref('info')
const forgotLoading = ref(false)

const handleLogin = async () => {
  feedback.value = ''
  try {
    await userStore.login(useremail.value, password.value);
    router.push({ name: 'HomePage' });
  } catch (err) {
    console.error('Login error:', err);
    feedback.value = err.message || 'An error occurred during login.'
  }
}

function openForgotDialog() {
  forgotEmail.value = useremail.value || ''
  forgotFeedback.value = ''
  forgotFeedbackType.value = 'info'
  forgotDialog.value = true
}

async function sendResetEmail() {
  forgotFeedback.value = ''
  if (!forgotEmail.value) {
    forgotFeedbackType.value = 'error'
    forgotFeedback.value = 'Please enter an email address.'
    return
  }
  forgotLoading.value = true
  try {
    const auth = getAuth()
    await sendPasswordResetEmail(auth, forgotEmail.value)
    forgotFeedbackType.value = 'success'
    forgotFeedback.value = 'Password reset email sent. Check your inbox.'
  } catch (err) {
    console.error('Error sending password reset:', err)
    forgotFeedbackType.value = 'error'
    if (err && err.code === 'auth/user-not-found') {
      forgotFeedback.value = 'No account found for that email.'
    } else if (err && err.message) {
      forgotFeedback.value = err.message
    } else {
      forgotFeedback.value = 'Unable to send reset email. Try again later.'
    }
  } finally {
    forgotLoading.value = false
  }
}
</script>

<style scoped>
.auth-page {
  min-height: 100vh;
  background: #F7F0E6;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  font-family: 'DM Sans', sans-serif;
}

.auth-brand {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 28px;
}

.auth-brand__mark {
  font-family: 'DM Sans', sans-serif;
  font-weight: 700;
  font-size: 2rem;
  color: #2D6A4F;
  letter-spacing: -0.5px;
  line-height: 1;
}

.auth-brand__sub {
  font-family: 'DM Mono', monospace;
  font-size: 0.7rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #6B7280;
  margin-top: 4px;
}

.auth-card {
  width: 100%;
  max-width: 400px;
  border-radius: 12px !important;
  overflow: hidden;
}

.auth-card-header {
  background: #2D6A4F;
  padding: 24px 24px 20px;
  color: white;
}

.auth-card-header h2 {
  font-family: 'DM Sans', sans-serif;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 4px;
}

.auth-card-header p {
  font-size: 0.875rem;
  opacity: 0.85;
  margin: 0;
}

.auth-links {
  margin-top: 20px;
  display: flex;
  gap: 8px;
  align-items: center;
  font-size: 0.875rem;
}

.auth-links a {
  color: #2D6A4F;
  font-weight: 600;
  text-decoration: none;
}

.auth-links a:hover {
  text-decoration: underline;
}

.auth-footer {
  margin-top: 12px;
  font-size: 0.8rem;
}

.auth-footer a {
  color: #6B7280;
  text-decoration: none;
}

.auth-footer a:hover {
  color: #2D6A4F;
}

.auth-extra {
  margin-top: 28px;
  font-size: 0.8rem;
  color: #6B7280;
  display: flex;
  gap: 6px;
}

.auth-extra a {
  color: #2D6A4F;
  text-decoration: none;
  font-weight: 600;
}

.auth-extra a:hover {
  text-decoration: underline;
}
</style>
